import copy from 'copy-to-clipboard';

/**
 * A robust utility for copying content to the clipboard.
 * It prioritizes the modern `navigator.clipboard` API and supports asynchronous
 * data resolution to prevent "User Activation" expiration. If the modern API
 * is unavailable or fails, it falls back to `copy-to-clipboard` which uses `execCommand`.
 * @param value - The content to copy. Can be a string, a Blob, or a Promise resolving to either.
 * @param type - The MIME type of the content. Required for `ClipboardItem` support. Defaults to `text/plain`.
 * @returns A promise resolving to `true` if the copy succeeded, `false` otherwise.
 *
 * @example
 * // 1. Basic text copy
 * await smartCopy("Hello World");
 * @example
 * // 2. Copying data after a network request
 * // WRONG: The user gesture might expire while waiting for the network.
 * // const data = await getUrl();
 * // const success = await smartCopy(data);
 *
 * // RIGHT: Pass the promise directly. The clipboard is locked in immediately.
 * const success = await smartCopy(getUrl());
 * @example
 * // 3. Copying an image blob
 * const blobPromise = getImageBlob();
 * await smartCopy(blobPromise, 'image/png');
 */
export const smartCopy = async (
  value: string | Promise<string> | Blob | Promise<Blob>,
  type: 'text/plain' | 'image/png' = 'text/plain'
): Promise<boolean> => {
  try {
    // 1. Modern API (Supports Promises and Images)
    if (navigator.clipboard && window.ClipboardItem) {
      const dataPromise = value instanceof Promise ? value : Promise.resolve(value);

      try {
        const item = new ClipboardItem({
          [type]: dataPromise.then((data) => {
            // Ensure data is a Blob for ClipboardItem
            return data instanceof Blob ? data : new Blob([data], { type });
          })
        });
        await navigator.clipboard.write([item]);
        return true;
      } catch (err) {
        console.warn('ClipboardItem failed, attempting fallback...', err);
        // Fall through to Clipboard or Legacy if it's text, otherwise fail
      }
    }

    const resolvedValue = value instanceof Promise ? await value : value;

    if (typeof resolvedValue === 'string') {
      try {
        // 2. Copy text via Clipboard
        await navigator.clipboard.writeText(resolvedValue);
        return true;
      } catch (err) {
        console.warn('Clipboard failed, attempting fallback...', err);
        // 3. Legacy Fallback
        return copy(resolvedValue);
      }
    }

    // Image (blob) cannot be copied via legacy execCommand fallback
    return false;
  } catch (error) {
    // This catches Promise rejections (e.g., Azure function errors)
    // or any other unexpected issues.
    console.error('Copy failed:', error);
    return false;
  }
};
